import {
  CaretRightOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Col, Collapse, Dropdown, Image, Row } from "antd";
import { memo, useEffect, useState } from "react";
import {
  AiFillEdit,
  AiOutlineBell,
  AiOutlinePushpin,
  AiOutlineUserAdd,
} from "react-icons/ai";
import apiFactory from "../../../api";
import AvatarComponent from "../../../components/avatar/AvatarComponent";
import { PreviewFile } from "../../../components/chat/PreviewFile";
import { PreviewVideoContent } from "../../../components/chat/PreviewVideoContent";
import {
  conversationType,
  MESSAGE_STATUS,
  PANEL_STATUS,
  role,
} from "../../../config/Constant";
import { useChatContext } from "../../../context/ChatContext";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { handleCopy } from "../../../utils/Utils";
import { ChatPreviewLink } from "./ChatPreviewLink";
import { useGlobalDownloader } from "../../../context/global/DownloadProvider";
import "./style.scss";
import MediaViewerModal from "../../../components/modal/MediaViewerModal";
import { useMediaResource } from "../../../hooks/useMediaResource";

const ImagePanel = ({ resourceList, jumpToMessage, isMail = false }) => {
  const { languageMap } = useInfoUser();
  const mediaResource = useMediaResource();
  const downloader = useGlobalDownloader();
  const { setMediaStorageType, setPanelStatus } = useMenuContext();

  const openModal = async (imageData) => {
    const mainPath =
      imageData?.mainPath ||
      imageData?.thumbnailPreviewPath ||
      imageData?.media?.mainPath;

    let mediaData = imageData;
    if (mainPath) {
      try {
        const result = await apiFactory.conversationApi.detailResource({
          mainPath,
        });
        if (result?.data) {
          mediaData = result?.data;
        }
      } catch (err) {
        console.error("Error fetching detail resource:", err);
      }
    }

    mediaResource?.setSelectedMedia(mediaData);
    mediaResource?.setMediaTypes(["IMAGE"]);
    mediaResource?.setIsFetchData(true);
    mediaResource?.setVisible(true);
  };

  if (!resourceList?.length) return null;

  return (
    <div>
      <Row>
        {resourceList?.map((img, i) => (
          <Col
            key={img?.mainPath || img?.resourceId || img?.media?.mainPath || i}
            span={8}
            className="px-[3px] h-[70px] parent-action-menu"
          >
            <Image
              src={
                img?.thumbnailPreviewPath ||
                img?.mainPath ||
                img?.media?.thumbnailPreviewPath ||
                img?.media?.mainPath
              }
              className="rounded cursor-pointer object-cover border border-gray-300"
              preview={false}
              width="100%"
              height="100%"
              onClick={() => openModal(img)}
            />
            <ActionMenu
              file={img}
              downloader={downloader}
              jumpToMessage={jumpToMessage}
              isMail={isMail}
            />
          </Col>
        ))}
      </Row>

      <Button
        className="w-full mt-2"
        onClick={() => {
          setMediaStorageType("IMAGE");
          setPanelStatus(PANEL_STATUS.MEDIA_STORAGE);
        }}
      >
        {languageMap?.["modal.message.showMore"] ?? "show more"}
      </Button>

      {/* Modal viewer */}
      {mediaResource?.visible && (
        <MediaViewerModal mediaResource={mediaResource} />
      )}
    </div>
  );
};

const FilePanel = ({ resourceList, jumpToMessage, contentType, isMail = false }) => {
  const { languageMap } = useInfoUser();
  const { setMediaStorageType, setPanelStatus } = useMenuContext();

  if (!resourceList?.length) return null;

  return (
    <div>
      <div>
        {resourceList?.map((file, i) => (
          <FileItem
            key={file?.mainPath || file?.resourceId || file?.messageId || i}
            file={file}
            idx={i}
            jumpToMessage={jumpToMessage}
            isMail={isMail}
          />
        ))}
      </div>

      <Button
        className="w-full mt-2"
        onClick={() => {
          setMediaStorageType(contentType);
          setPanelStatus(PANEL_STATUS.MEDIA_STORAGE);
        }}
      >
        {languageMap?.["modal.message.showMore"] ?? "show more"}
      </Button>
    </div>
  );
};

function ActionCircle({ icon, text, disabled, onClick }) {
  return (
    <div className="flex flex-col items-center text-xs">
      <Button
        shape="circle"
        icon={icon}
        size="large"
        disabled={disabled}
        onClick={onClick}
      />
      <span
        className={`mt-1 text-center w-16 ${disabled ? "text-gray-500" : "text-gray-600"}`}
      >
        {text}
      </span>
    </div>
  );
}

export const FileItem = memo(({ file, idx, jumpToMessage, isMail = false }) => {
  const downloader = useGlobalDownloader();
  return (
    <div className="relative " key={idx}>
      {file?.contentType === "VIDEO" ? (
        <PreviewVideoContent
          file={{
            ...file,
            createdAt: file?.createdAt,
            userId: file?.senderId,
          }}
          isSelf={true}
          isInPanel={true}
        />
      ) : (
        <PreviewFile
          requestUuid={file?.requestId}
          content={null}
          file={file}
          contentType={MESSAGE_STATUS.FILE}
          percentProgress={0}
          totalVolume={file?.volume}
          path="conversation"
          isPinned={false}
          isInPanel={true}
        />
      )}

      <ActionMenu
        file={file}
        downloader={downloader}
        jumpToMessage={jumpToMessage}
        isMail={isMail}
      />
    </div>
  );
});

export const LinkItem = memo(({ link, idx, jumpToMessage, isMail = false }) => {
  const downloader = useGlobalDownloader();

  return (
    <div className="relative parent-action-menu" key={idx}>
      <ChatPreviewLink previewLink={link?.linkPreview} isInPanel={true} />

      <ActionMenu
        file={link}
        downloader={downloader}
        actions={["copy"]}
        jumpToMessage={jumpToMessage}
        isMail={isMail}
      />
    </div>
  );
});

export const ActionMenu = memo(
  ({ file, downloader, actions = [], jumpToMessage, isMail = false }) => {
    const { languageMap } = useInfoUser();
    const { startDownload } = downloader;

    const onMenuClick = (keyDom) => {
      const { key } = keyDom;

      switch (key) {
        case "share":
          console.log("Chia sẻ");
          break;

        case "save":
          console.log("Lưu Cloud");
          break;

        case "origin":
          if (!isMail && file?.messageId && jumpToMessage) {
            jumpToMessage({
              messageId: file?.messageId,
              requestUuid: file?.requestUuid,
              createdAt: file?.createdAt,
            });
          }
          break;

        case "copy":
          handleCopy(file?.linkPreview?.url || "");
          break;

        case "select":
          console.log("Chọn nhiều");
          break;

        case "download":
          if (file?.media?.resourceId) {
            startDownload(
              file?.media?.resourceId,
              file?.originalName || file?.fileName || file?.media?.originalName,
            );
          }
          break;
        default:
          console.log("Không có action");
      }
    };

    const defaultItems = isMail
      ? []
      : [
        {
          key: "origin",
          label: languageMap?.["conversation.message.jump"] ?? "Jump to message",
          icon: <FileSearchOutlined />,
        },
      ];

    const extraItems = actions
      ?.map((act) => {
        switch (act) {
          case "copy":
            return { key: "copy", label: "copy", icon: <CopyOutlined /> };
          case "download":
            return {
              key: "download",
              label:
                languageMap?.["conversation.message.media.download"] ??
                "Download",
              icon: <DownloadOutlined />,
            };
          default:
            return null;
        }
      })
      .filter(Boolean);

    const menuItems = [...defaultItems, ...extraItems];
    if (!menuItems.length) return null;

    return (
      <div className="absolute top-1 right-1 children-action-menu">
        <Dropdown
          menu={{
            items: menuItems,
            onClick: onMenuClick,
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            className="!bg-white hover:!bg-white active:!bg-white !shadow rounded-full border border-gray"
          />
        </Dropdown>
      </div>
    );
  },
);

const PanelView = ({ jumpToMessage, openModal, isMail = false }) => {
  const {
    selectedConversation,
    groupInfoResources,
    setGroupInfoResources,
  } = useMenuContext();
  const chatContext = useChatContext() || {};
  const [localResources, setLocalResources] = useState({
    images: [],
    files: [],
    audios: [],
    videos: [],
    linkPreviews: [],
  });

  const isMailMode =
    isMail || selectedConversation?.type === conversationType.NOTIFICATION;

  const resources = isMailMode
    ? groupInfoResources || localResources
    : chatContext.resources || localResources;
  const setResources = isMailMode
    ? setGroupInfoResources || setLocalResources
    : chatContext.setResources || setLocalResources;
  const [openCollapses, setOpenCollapses] = useState([1, 2, 3, 4]);
  const { languageMap } = useInfoUser();

  const getRepresentResource = async () => {
    if (!selectedConversation?.conversationId) return;
    try {
      const result = await apiFactory.conversationApi.getRepresentResource(
        selectedConversation?.conversationId,
      );
      setResources(result?.data);
    } catch (err) {
      console.error("Error loading resources:", err);
    }
  };

  const collapseItem = [
    {
      key: 1,
      label: "Image",
      children: (
        <ImagePanel
          resourceList={resources?.images}
          jumpToMessage={jumpToMessage}
          isMail={isMail}
        />
      ),
    },

    {
      key: 2,
      label: "Video",
      children: (
        <FilePanel
          resourceList={resources?.videos}
          jumpToMessage={jumpToMessage}
          contentType="VIDEO"
          isMail={isMail}
        />
      ),
    },
    {
      key: 3,
      label: "File",
      children: (
        <FilePanel
          resourceList={resources?.files}
          jumpToMessage={jumpToMessage}
          contentType="FILE"
          isMail={isMail}
        />
      ),
    },
    {
      key: 4,
      label: "Audio",
      children: (
        <FilePanel
          resourceList={resources?.audios}
          jumpToMessage={jumpToMessage}
          contentType="AUDIO"
          isMail={isMail}
        />
      ),
    },
  ];

  useEffect(() => {
    getRepresentResource();
  }, [selectedConversation?.conversationId]);

  return (
    <div className="mb-[60px]">
      <div className="p-4 border-b-[8px] border-[#eaeef0] flex flex-col items-center">
        <div className="flex flex-col items-center">
          <AvatarComponent conversation={selectedConversation} />
          <div className="flex items-center mt-2 gap-2 ">
            <span className="text-center font-semibold">
              {selectedConversation?.name}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        {!isMail && (
          <div className="mt-4 flex justify-around w-full">
            <ActionCircle
              icon={<AiOutlineBell size={20} />}
              text={
                languageMap?.["select.conversation.option.turnOnNotifications"] ??
                "Turn on notifications"
              }
              disabled
            />
            <ActionCircle
              icon={<AiOutlinePushpin size={20} />}
              text={
                languageMap?.["select.conversation.option.pinnedConversation"] ??
                "Pin conversation"
              }
              disabled
            />
            <ActionCircle
              icon={<AiOutlineUserAdd size={20} />}
              text={
                languageMap?.["select.conversation.option.addMember"] ??
                "Add members"
              }
              disabled
            />
            {selectedConversation?.type !== conversationType.SYSTEM &&
              selectedConversation?.conversationStatus === "ACTIVE" &&
              (selectedConversation?.role === role?.HOST ||
                selectedConversation?.type === conversationType.PERSONAL) && (
                <button onClick={openModal}>
                  <div className="icon-edit">
                    <ActionCircle
                      icon={<AiFillEdit onClick={openModal} size={20} />}
                      text="Edit group"
                    />
                  </div>
                </button>
              )}
          </div>
        )}
      </div>
      {/* Collapse sections */}
      <Collapse
        items={collapseItem}
        activeKey={openCollapses}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        bordered={false}
        onChange={setOpenCollapses}
      />
    </div>
  );
};

export { PanelView };
